import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Box, Paper, TextField, InputAdornment, IconButton, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import SendIcon from '@mui/icons-material/Send';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import ThumbDownOutlinedIcon from '@mui/icons-material/ThumbDownOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { keyframes } from '@mui/system';

const typingDots = keyframes`
  0% {
    opacity: 0;
    transform: scale(0.65) translateY(-2px);
  }
  18% {
    opacity: 0.85;
    transform: scale(0.95) translateY(-3px);
  }
  38% {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
  58% {
    opacity: 0.8;
    transform: scale(0.92) translateY(2px);
  }
  78% {
    opacity: 0.55;
    transform: scale(0.85) translateY(3px);
  }
  100% {
    opacity: 0;
    transform: scale(0.7) translateY(1px);
  }
`;

const checkPop = keyframes`
  0% {
    opacity: 0;
    transform: scale(0.6);
  }
  55% {
    opacity: 1;
    transform: scale(1.12);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
`;

const stripImageMarkup = text =>
  String(text ?? '')
    .replace(/!\[[^\]]*\]\([^)]+\)/g, '')
    .replace(/!\[[^\]]*\]\[[^\]]*\]/g, '')
    .replace(/<img\b[^>]*>/gi, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

function MessageBubble({ role, content, localRole, senderName, showSenderMeta }) {
  const isLocal = role === localRole;
  const shouldShowMeta = Boolean(isLocal && showSenderMeta && senderName);
  const sanitizedContent = stripImageMarkup(content);
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: isLocal ? 'flex-end' : 'flex-start',
        mb: 1.5,
      }}
    >
      {shouldShowMeta && (
        <Typography
          variant='caption'
          color='text.secondary'
          sx={{ mb: 0.25, textAlign: isLocal ? 'right' : 'left' }}
        >
          {senderName}
        </Typography>
      )}
      <Paper
        elevation={0}
        sx={{
          maxWidth: '820px',
          p: 1.5,
          borderRadius: 2,
          backgroundColor: theme =>
            isLocal
              ? alpha(theme.palette.success.main, theme.palette.mode === 'light' ? 0.18 : 0.15)
              : theme.palette.mode === 'light'
                ? theme.palette.background.paper
                : 'rgba(255,255,255,0.06)',
          border: theme =>
            `1px solid ${
              theme.palette.mode === 'light' ? theme.palette.divider : 'rgba(255,255,255,0.08)'
            }`,
          whiteSpace: 'pre-wrap',
        }}
      >
        <Typography variant='body1' sx={{ fontSize: 15, lineHeight: 1.5 }}>
          {sanitizedContent}
        </Typography>
      </Paper>
    </Box>
  );
}

function TypingIndicator() {
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0.6,
        px: 1.5,
        py: 0.75,
        borderRadius: 2,
        backgroundColor: 'rgba(255,255,255,0.16)',
        minWidth: 72,
      }}
    >
      {[0, 1, 2].map(index => (
        <Box
          key={index}
          sx={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            backgroundColor: theme => theme.palette.grey[400],
            opacity: 0,
            animation: `${typingDots} 1.85s infinite cubic-bezier(0.3, 0.7, 0.2, 1)`,
            animationDelay: `${index * 0.28}s`,
          }}
        />
      ))}
    </Box>
  );
}

function ChatRatingPrompt({ selected, disabled, onRate, showThanks, animateThanks }) {
  if (showThanks) {
    return (
      <Paper
        elevation={0}
        sx={{
          mx: 'auto',
          width: '100%',
          maxWidth: 420,
          mb: 1,
          p: 1.25,
          borderRadius: 2,
          backgroundColor: 'rgba(16,163,127,0.10)',
          border: '1px solid rgba(16,163,127,0.22)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
        }}
      >
        <CheckCircleIcon
          sx={{
            color: 'primary.main',
            animation: animateThanks ? `${checkPop} 420ms ease-out` : 'none',
          }}
        />
        <Typography variant='body2' sx={{ fontWeight: 600 }}>
          Danke für deine Bewertung!
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        mx: 'auto',
        width: '100%',
        maxWidth: 420,
        mb: 1,
        p: 1.25,
        borderRadius: 2,
        backgroundColor: theme =>
          theme.palette.mode === 'light' ? theme.palette.background.paper : 'rgba(255,255,255,0.06)',
        border: theme =>
          `1px solid ${
            theme.palette.mode === 'light' ? theme.palette.divider : 'rgba(255,255,255,0.10)'
          }`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1.25,
      }}
    >
      <Typography variant='body2' color='text.secondary' sx={{ mr: 0.5 }}>
        War das hilfreich?
      </Typography>
      <IconButton
        size='small'
        onClick={() => onRate(true)}
        disabled={disabled}
        sx={{
          color: selected === 'up' ? 'primary.main' : 'text.secondary',
          backgroundColor: selected === 'up' ? 'rgba(25, 118, 210, 0.12)' : 'transparent',
          border: '1px solid',
          borderColor: selected === 'up' ? 'rgba(25, 118, 210, 0.28)' : 'rgba(255,255,255,0.10)',
          '&:hover': {
            backgroundColor:
              selected === 'up' ? 'rgba(25, 118, 210, 0.16)' : 'rgba(255,255,255,0.08)',
          },
        }}
      >
        <ThumbUpOutlinedIcon fontSize='small' />
      </IconButton>
      <IconButton
        size='small'
        onClick={() => onRate(false)}
        disabled={disabled}
        sx={{
          color: selected === 'down' ? 'primary.main' : 'text.secondary',
          backgroundColor: selected === 'down' ? 'rgba(25, 118, 210, 0.12)' : 'transparent',
          border: '1px solid',
          borderColor: selected === 'down' ? 'rgba(25, 118, 210, 0.28)' : 'rgba(255,255,255,0.10)',
          '&:hover': {
            backgroundColor:
              selected === 'down' ? 'rgba(25, 118, 210, 0.16)' : 'rgba(255,255,255,0.08)',
          },
        }}
      >
        <ThumbDownOutlinedIcon fontSize='small' />
      </IconButton>
    </Paper>
  );
}

export default function ChatConversation({
  messages = [],
  inputValue,
  onInputChange,
  onSend,
  onKeyDown,
  inputDisabled,
  canSend,
  inputRef,
  placeholder,
  isTyping,
  sessionEnded,
  sessionEndedText,
  containerMaxWidth = 820,
  localRole = 'user',
  showLocalSenderNames = false,
  rating,
  contentSx,
}) {
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length, isTyping]);

  return (
    <>
      <Box
        ref={scrollRef}
        sx={{
          flex: 1,
          overflowY: 'auto',
          py: 2,
        }}
      >
        <Box sx={{ maxWidth: containerMaxWidth, mx: 'auto', width: '100%', ...(contentSx || {}) }}>
          {messages.map((m, idx) => (
            <MessageBubble
              key={idx}
              role={m.role}
              content={m.content}
              localRole={localRole}
              senderName={m.senderName}
              showSenderMeta={showLocalSenderNames}
            />
          ))}
          {isTyping && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', py: 1 }}>
              <TypingIndicator />
            </Box>
          )}
        </Box>
      </Box>
      <Box
        sx={{
          position: 'sticky',
          bottom: 0,
          pt: 1,
          pb: 2,
          background: 'transparent',
        }}
      >
        {rating && (rating.show || rating.showThanks) && (
          <ChatRatingPrompt
            selected={rating.selected}
            disabled={!!rating.disabled}
            onRate={rating.onRate}
            showThanks={!!rating.showThanks}
            animateThanks={!!rating.animateThanks}
          />
        )}
        {sessionEnded && sessionEndedText && (
          <Paper
            elevation={0}
            sx={{
              maxWidth: containerMaxWidth,
              mx: 'auto',
              width: '100%',
              mb: 1,
              p: 1.5,
              borderRadius: 2,
              backgroundColor: 'rgba(211, 47, 47, 0.1)',
              border: '1px solid rgba(211, 47, 47, 0.3)',
              textAlign: 'center',
            }}
          >
            <Typography variant='body2' sx={{ color: '#ff8a80' }}>
              {sessionEndedText}
            </Typography>
          </Paper>
        )}
        <Paper
          elevation={0}
          sx={{
            maxWidth: containerMaxWidth,
            mx: 'auto',
            width: '100%',
            p: 1,
            borderRadius: 3,
            backgroundColor: theme =>
              theme.palette.mode === 'light' ? theme.palette.background.paper : 'rgba(0,0,0,0.2)',
            border: theme =>
              `1px solid ${
                theme.palette.mode === 'light' ? theme.palette.divider : 'rgba(255,255,255,0.12)'
              }`,
          }}
        >
          <TextField
            inputRef={inputRef}
            value={inputValue}
            onChange={onInputChange}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            fullWidth
            multiline
            minRows={1}
            maxRows={6}
            size='small'
            disabled={inputDisabled}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': { border: 'none' },
                '&:hover fieldset': { border: 'none' },
                '&.Mui-focused fieldset': { border: 'none' },
              },
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position='end'>
                  <IconButton color='primary' onClick={onSend} disabled={!canSend}>
                    <SendIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Paper>
      </Box>
    </>
  );
}

ChatConversation.propTypes = {
  messages: PropTypes.arrayOf(
    PropTypes.shape({
      role: PropTypes.string,
      content: PropTypes.string,
      senderName: PropTypes.string,
    })
  ),
  inputValue: PropTypes.string.isRequired,
  onInputChange: PropTypes.func.isRequired,
  onSend: PropTypes.func.isRequired,
  onKeyDown: PropTypes.func,
  inputDisabled: PropTypes.bool,
  canSend: PropTypes.bool,
  inputRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({
      current: PropTypes.any,
    }),
  ]),
  placeholder: PropTypes.string,
  isTyping: PropTypes.bool,
  sessionEnded: PropTypes.bool,
  sessionEndedText: PropTypes.string,
  containerMaxWidth: PropTypes.number,
  localRole: PropTypes.string,
  showLocalSenderNames: PropTypes.bool,
  rating: PropTypes.shape({
    show: PropTypes.bool,
    showThanks: PropTypes.bool,
    animateThanks: PropTypes.bool,
    disabled: PropTypes.bool,
    selected: PropTypes.oneOf(['up', 'down', null]),
    onRate: PropTypes.func,
  }),
  contentSx: PropTypes.object,
};
